<template>
  <div class="file-loader">
    <transition-group :duration="duration" tag="div" name="fade" class="file-loader__items">
      <div v-for="link in linksList" :key="link.id" class="file-loader-item">
        <file-preview
          :type="link.type"
          :url="link.url"
          :name="link.name"
          :actions="actions"
          data-type="link"
          @click="showGallery(link)"
        ></file-preview>

        <c-button
          v-if="!disabled"
          v-b-popover.html="{
            container: '#app',
            trigger: 'hover blur',
            content: 'Удалить',
            placement: 'top',
          }"
          class="file-loader-item__remove"
          size="iconic"
          icon="trash"
          @click="removeLink(link)"
        />
      </div>

      <div v-for="file in files" :key="file.id" class="file-loader-item">
        <file-preview
          :type="file.type"
          :url="file.url"
          :name="file.name"
          :actions="actions"
          data-type="file"
          @click="showGallery(file)"
        ></file-preview>

        <c-button
          v-if="!disabled"
          v-b-popover.html="{
            container: '#app',
            trigger: 'hover blur',
            content: 'Удалить',
            placement: 'top',
          }"
          class="file-loader-item__remove"
          size="iconic"
          icon="trash"
          @click="removeFile(file)"
        />
      </div>
    </transition-group>

    <input
      ref="input"
      name="attachment[]"
      :multiple="limit > 1"
      type="file"
      :accept="inputAccept"
      hidden
      @change="onFileChange"
    />

    <div class="file-loader__footer">
      <c-button
        class="file-loader__add"
        :class="{
          'file-loader__add--error': errors.length,
        }"
        :disabled="disabled || limitReached"
        :data-slide="slide"
        size="small"
        :icon="{ icon: 'plus', color: 'primary' }"
        :label="addFileLabel || 'Добавить файл'"
        @click="chooseFiles()"
      />

      <slot name="note"></slot>
    </div>
    <transition name="fade">
      <div v-if="errors.length" class="file-loader__errors">
        <c-error v-for="(error, index) in errors" :key="index" :text="error" />
      </div>
    </transition>

    <FsLightbox
      :key="gallery.length"
      :toggler="lightbox"
      :sources="gallery"
      :slide="slide"
      :disable-local-storage="true"
    />
  </div>
</template>

<script>
import FsLightbox from "fslightbox-vue";

import Button from "@/components/controls2/Button";
import ErrorFeedback from "@/components/error-feedback";
import FilePreview from "@/components/file-preview";

import * as fileUtils from "@/utils/files";

let unique = 1;

export default {
  name: "documents-create-edit-document-modal",
  components: {
    FsLightbox,
    "c-button": Button,
    "c-error": ErrorFeedback,
    "file-preview": FilePreview,
  },
  props: ["value", "accept", "format", "limit", "actions", "links", "disabled", "addFileLabel"],

  data() {
    return {
      files: [],
      acceptExtensions: [],
      inputAccept: "*",
      errors: [],

      linksList: [],

      slide: 0,
      lightbox: false,

      maxCount: 20,

      duration: 400,
    };
  },

  computed: {
    count() {
      return this.files.length + this.linksList.length;
    },
    limitReached() {
      return this.maxCount && this.count >= this.maxCount;
    },
    filesWithPreview() {
      return [...this.linksList.filter((link) => link.hasPreview), ...this.files.filter((file) => file.hasPreview)];
    },
    gallery() {
      const gallery = this.filesWithPreview.map((file) => file.url);
      return gallery;
    },
  },

  watch: {
    files(v) {
      const files = v.map((f) => f.file);
      this.$emit("input", files);
    },
    links(v) {
      if (this.compareLinks(v, this.linksList)) return;
      this.linksList = this.formatLinks(v);
    },
    linksList(v) {
      const ids = v.map((l) => l.id);
      const oldLinks = this.links || [];
      const oldIds = oldLinks.map((l) => l.id);

      if (ids.join("") === oldIds.join("")) return;

      const newLinks = oldLinks.filter((l) => ids.includes(l.id));

      this.$emit("changeLinks", newLinks);
    },
  },

  created() {
    this.linksList = this.formatLinks(this.links);

    if (this.limit) this.maxCount = this.limit;

    this.files = this.value || [];

    if (this.accept) {
      this.acceptExtensions = String(this.accept).split(",");
      this.inputAccept = this.accept;
    } else if (this.format) {
      const format = new Set(this.format.split(","));

      if (format.has("all")) {
        format.add("images");
        format.add("video");
        format.add("pdf");
        format.add("docs");
      }

      let accepts = [];
      [...format].forEach((f) => {
        if (f === "images") {
          accepts = [...accepts, ...fileUtils.IMAGE_EXTENSIONS];
        } else if (f === "video") {
          accepts = [...accepts, ...fileUtils.VIDEO_EXTENSIONS];
        } else if (f === "docs") {
          accepts = [...accepts, ...fileUtils.DOCS_EXTENSIONS];
        } else if (f === "pdf") {
          accepts = [...accepts, fileUtils.PDF_EXTENSION];
        }
      });

      if (accepts.length) {
        this.acceptExtensions = accepts;
        this.inputAccept = accepts.map((ext) => `.${ext}`).join(",");
      }
    }
  },

  methods: {
    formatLinks(links) {
      return (links || []).map((params) => {
        const { id, url, name } = params;
        const type = fileUtils.getFileType(url);
        return {
          id,
          type,
          url,
          name: name || url.split("/").pop(),
          hasPreview: type === "image" || type === "video",
        };
      });
    },
    compareLinks(links1, links2) {
      const ids1 = (links1 || []).map((l) => l.id).join("");
      const ids2 = (links2 || []).map((l) => l.id).join("");

      return ids1 === ids2;
    },
    chooseFiles() {
      this.$refs.input.click();
    },

    reset() {
      this.$refs.input.value = null;
    },

    checkSize(file) {
      const ext = fileUtils.getFileExtension(file.name);
      if (fileUtils.isVideo(ext)) {
        if (file.size > 10 * 1024 * 1024) {
          this.pushError(file, "Размер видео не должен превышать 10Мб");
          return false;
        }
        return true;
      }

      if (file.size > 5 * 1024 * 1024) {
        this.pushError(file, "Размер файла не должен превышать 5Мб");
        return false;
      }

      return true;
    },

    checkExtension(file) {
      const ext = fileUtils.getFileExtension(file.name);
      const accepted = this.acceptExtensions;
      console.log(this.acceptExtensions, ext);
      if (!!accepted.length && !accepted.includes(ext)) {
        this.pushError(file, `Поддерживаются только следующие расширения файлов: ${accepted.join(", ")}`);

        return false;
      }

      return true;
    },

    pushError (file, text) {
      const {name} = file;

      this.errors.push(`[${name}] ${text}`);
    },

    onFileChange(e) {
      this.errors = [];
      const files = e.target.files || e.dataTransfer.files;

      if (!files.length) return;

      let newFileList = Array.from(files);

      if (this.maxCount) {
        newFileList = newFileList.slice(0, this.maxCount - this.count);
      }

      newFileList.forEach((file) => {
        if (!this.checkSize(file)) return;
        if (!this.checkExtension(file)) return;
        this.addFile(file);
      });

      this.reset();
    },

    addFile(file) {
      if (this.maxCount && this.count >= this.maxCount) return;

      new Promise((res) => {
        const type = fileUtils.getFileType(file.name);
        unique += 1;
        const fileObject = {
          id: `file-${unique}`,
          file,
          name: file.name,
          hasPreview: false,
          type,
          url: null,
        };

        if (type === "image" || type === "video") {
          fileObject.hasPreview = true;
          this.createPreview(file).then((url) => {
            fileObject.url = url;
            res(fileObject);
          });
        } else {
          const blob = new Blob([file], { type: file.type });
          const href = URL.createObjectURL(blob);
          fileObject.url = href;
          res(fileObject);
        }
      }).then((item) => {
        this.files.push(item);
      });
    },

    createPreview(file) {
      return new Promise((res) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          res(event.target.result);
        };
        reader.readAsDataURL(file);
      });
    },

    removeFile(file) {
      this.errors = [];
      this.files = this.files.filter((f) => f !== file);
    },

    removeLink(link) {
      this.linksList = this.linksList.filter((l) => l !== link);
    },

    showGallery(file) {
      const index = this.filesWithPreview.indexOf(file);
      if (index === -1) return;
      this.slide = index + 1;
      this.lightbox = !this.lightbox;
    },

    clear() {
      this.duration = 0;
      this.files = [];
      this.linksList = [];
      this.$nextTick(() => {
        this.duration = 400;
      });
    },

    clearFiles() {
      this.duration = 0;
      this.files = [];
      this.$nextTick(() => {
        this.duration = 400;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";
.file-loader {
  &__items {
    margin-left: -6px;
    margin-right: -6px;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
  }

  &-item {
    display: flex;
    margin: 0 6px 25px;

    &__remove {
      margin-left: 7px;
      margin-top: -4px;
    }
  }

  &__add {
    flex-shrink: 0;
    &-required,
    &--error {
      border: solid 1px #ff238d;
    }

    &-photos-not-empty {
      margin-top: -5px;

      @media screen and (max-width: 700px) {
        margin-top: -10px;
      }
    }
  }

  &__footer {
    display: flex;
  }

  @include onlyMobile {
    &__items {
      margin-right: -16px;
    }
    &-item {
      margin-right: 5px;
      margin-bottom: 20px;
    }
    &__footer {
      display: block;
    }
  }
}
</style>
